import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useReducedMotion, AnimatePresence } from "framer-motion";
import { CalendarCheck, Play, Apple } from "lucide-react";
import WhatsAppIcon from "./WhatsAppIcon";
import PhoneMockup from "./PhoneMockup";
import { useSite } from "@/lib/site";
import { waLink } from "@/lib/api";
import { EASE } from "./Reveal";

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.85, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function StoreBadge({ url, label, icon: Icon, testid, hint }) {
  return (
    <a
      href={url || "#"}
      target={url ? "_blank" : undefined}
      rel="noreferrer"
      title={url ? label : hint}
      data-testid={testid}
      className="inline-flex items-center gap-2.5 rounded-xl border border-cream/20 bg-forest-900/60 px-4 py-2 transition hover:border-gold/60"
    >
      <Icon className="h-5 w-5 text-gold" />
      <span className="leading-tight">
        <span className="block text-[10px] text-cream/60">{hint}</span>
        <span className="block text-xs font-bold text-cream">{label}</span>
      </span>
    </a>
  );
}

export default function Hero() {
  const { tc, screenshots, lang } = useSite();
  const shots = screenshots || [];
  const [heroIdx, setHeroIdx] = useState(0);
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [fine, setFine] = useState(false);

  useEffect(() => {
    setFine(window.matchMedia("(hover: hover) and (pointer: fine)").matches);
  }, []);

  useEffect(() => {
    if (reduce || shots.length < 2) return;
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % shots.length), 4500);
    return () => clearInterval(id);
  }, [reduce, shots.length]);

  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const rotateX = useSpring(rx, { stiffness: 55, damping: 14 });
  const rotateY = useSpring(ry, { stiffness: 55, damping: 14 });

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPhone = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const c = tc?.hero;
  const wa = waLink(tc?.contact?.whatsappNumber, tc?.contact?.whatsappMessage);
  const isFr = lang === "fr";

  const onMove = (e) => {
    if (!fine || reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 8);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <section
      ref={ref}
      id="top"
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      className="grain relative overflow-hidden bg-forest-950 text-cream"
    >
      {/* cinematic lighting */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[460px] bg-[radial-gradient(60%_100%_at_50%_0%,rgba(212,175,55,0.14),transparent_70%)]"
        aria-hidden="true"
      />
      <motion.div style={{ y: yGlow }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-32 left-1/4 h-[480px] w-[480px] rounded-full bg-gold/15 blur-[140px]" />
        <div className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-forest-700/50 blur-[120px]" />
        <div className="absolute left-0 top-1/2 h-[300px] w-[300px] rounded-full bg-gold/10 blur-[100px]" />
      </motion.div>

      {/* floating metallic geometry */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="animate-drift absolute right-[6%] top-[14%] hidden h-72 w-72 rounded-full border border-gold/15 lg:block" />
        <div className="animate-drift absolute right-[10%] top-[22%] hidden h-72 w-72 rounded-full border border-cream/5 lg:block" style={{ animationDelay: "-6s" }} />
        <div className="absolute left-[4%] bottom-[16%] hidden h-40 w-40 rotate-45 rounded-[2.5rem] border border-gold/10 lg:block" />
        <div className="absolute left-[10%] top-[30%] hidden h-px w-40 bg-gradient-to-l from-transparent via-gold/25 to-transparent lg:block" />
      </div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 pb-28 pt-32 sm:pt-36 lg:grid-cols-[1.05fr_0.95fr] lg:gap-6 lg:pb-36 lg:pt-44">
        <div>
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-4 py-1.5 text-xs font-semibold text-gold sm:text-sm"
          >
            {c?.badge}
          </motion.span>

          <h1 className="mt-6 font-head text-4xl font-black leading-[1.2] sm:text-6xl lg:text-[4.2rem]">
            <MaskedLine delay={0.1}>{c?.titleTop}</MaskedLine>
            <MaskedLine delay={0.28}>
              <span className="gold-text-shimmer">{c?.titleBottom}</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
            className="mt-6 max-w-xl leading-relaxed text-cream/75 sm:text-lg"
            data-testid="hero-subtitle"
          >
            {c?.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-button"
              className="shine-sweep group relative inline-flex items-center gap-3 rounded-full bg-gold px-7 py-4 font-head text-base font-extrabold text-forest-950 shadow-[0_16px_40px_-10px_rgba(212,175,55,0.55)] transition hover:-translate-y-0.5 hover:bg-gold-light"
            >
              <WhatsAppIcon className="h-5 w-5 transition group-hover:scale-110" />
              {c?.ctaText}
            </a>
            <a
              href="#gallery"
              data-testid="hero-gallery-link"
              className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-7 py-4 text-sm font-semibold text-cream transition hover:border-gold hover:text-gold"
            >
              {isFr ? "Découvrir l'application" : "شاهد التطبيق"}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85, ease: EASE }}
            className="mt-5 flex flex-wrap items-center gap-3"
            data-testid="hero-store-badges"
          >
            <StoreBadge
              url={c?.googlePlayUrl}
              label="Google Play"
              hint={isFr ? "Télécharger sur" : "حمّله من"}
              icon={Play}
              testid="hero-googleplay-button"
            />
            <StoreBadge
              url={c?.appStoreUrl}
              label="App Store"
              hint={isFr ? "Télécharger sur" : "حمّله من"}
              icon={Apple}
              testid="hero-appstore-button"
            />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-4 text-xs text-cream/50"
            data-testid="hero-cta-note"
          >
            {c?.ctaNote}
          </motion.p>
        </div>

        <motion.div style={{ y: yPhone }} className="scene-3d relative flex justify-center lg:justify-start">
          <div className="absolute -inset-8 rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />
          <motion.div
            initial={{ opacity: 0, y: 60, rotate: 2 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 1, delay: 0.35, ease: EASE }}
            className="relative"
          >
            <div className="animate-float-soft">
              <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>
                <PhoneMockup
                  shot={shots[Math.min(heroIdx, Math.max(shots.length - 1, 0))]}
                  width={290}
                  caption={false}
                  crossfade
                  eager
                />
              </motion.div>
            </div>
            <div className="mx-auto -mt-4 h-14 w-2/3 rounded-[100%] bg-gold/15 blur-2xl" aria-hidden="true" />
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 1, ease: EASE }}
              className="absolute -right-6 top-16 hidden items-center gap-2 rounded-2xl border border-gold/25 bg-forest-900/90 px-4 py-3 text-xs font-semibold text-cream shadow-xl backdrop-blur sm:flex"
              data-testid="hero-floating-badge"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/15 text-gold">
                <CalendarCheck className="h-4 w-4" />
              </span>
              {isFr ? "Votre réservation confirmée" : "حجزك مؤكد وفي وقته"}
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-gold/40 to-transparent" aria-hidden="true" />
    </section>
  );
}
